import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const StepReviewQuestions = ({ campaignData, onDataChange, onSave, onMessage }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  const predefinedQuestions = [
    { id: 'overall', label: 'Overall Experience', required: true, alwaysEnabled: true },
    { id: 'quality', label: 'Quality of Service', required: false, alwaysEnabled: false },
    { id: 'professionalism', label: 'Professionalism & Courtesy', required: false, alwaysEnabled: false },
    { id: 'value', label: 'Value for Money', required: false, alwaysEnabled: false },
    { id: 'cleanliness', label: 'Cleanliness & Ambiance', required: false, alwaysEnabled: false },
    { id: 'punctuality', label: 'Punctuality', required: false, alwaysEnabled: false }
  ];

  // Initialize enabled questions from the questions array (normalized by campaign service)
  const getEnabledQuestionIds = () => {
    if (campaignData.questions && campaignData.questions.length > 0) {
      // Extract enabled question IDs from the normalized questions
      return campaignData.questions
        .filter(q => q.enabled)
        .map(q => {
          // Map question labels back to IDs
          const predefined = predefinedQuestions.find(pq => pq.label === q.label);
          return predefined ? predefined.id : null;
        })
        .filter(id => id !== null);
    }
    // Default fallback
    return ['overall', 'quality'];
  };
  
  const enabledQuestions = getEnabledQuestionIds();
  
  console.log('StepReviewQuestions - Campaign questions:', campaignData.questions);
  console.log('StepReviewQuestions - Derived enabled IDs:', enabledQuestions);

  // Validation functions
  const validateQuestionSelection = (questionIds) => {
    if (questionIds.length < 2) {
      setValidationError('At least 2 questions must be enabled');
      return false;
    } else if (questionIds.length > 4) {
      setValidationError('Maximum 4 questions can be enabled');
      return false;
    } else {
      setValidationError('');
      return true;
    }
  };

  const handleQuestionToggle = (questionId) => {
    const updatedEnabled = [...enabledQuestions];
    
    if (updatedEnabled.includes(questionId)) {
      // Don't allow disabling if it would leave us with less than 2 questions
      if (updatedEnabled.length > 2) {
        const index = updatedEnabled.indexOf(questionId);
        updatedEnabled.splice(index, 1);
      } else {
        setValidationError('At least 2 questions must be enabled');
        return;
      }
    } else {
      // Don't allow enabling if we already have 4 questions
      if (updatedEnabled.length < 4) {
        updatedEnabled.push(questionId);
      } else {
        setValidationError('Maximum 4 questions can be enabled');
        return;
      }
    }
    
    // Clear validation error if we get here
    validateQuestionSelection(updatedEnabled);
    
    onDataChange('enabled_questions', updatedEnabled);
    
    // Update the questions array with PROPER structure including 'enabled' field
    const allQuestionsWithEnabledState = predefinedQuestions.map(q => ({
      label: q.label,
      enabled: updatedEnabled.includes(q.id),
      required: q.required
    }));
    
    console.log('handleQuestionToggle - Updated questions with enabled state:', allQuestionsWithEnabledState);
    onDataChange('questions', allQuestionsWithEnabledState);
  };

  const getEnabledQuestionsCount = () => enabledQuestions.length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Selection</h3>
        <p className="text-gray-600">
          Configure the questions customers will see and how ratings determine the experience flow.
        </p>
      </div>

      {/* Request a Review Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Request a Review</h4>
        <p className="text-sm text-gray-600 mb-4">
          Ask for a review if rated..
        </p>
        
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="radio"
              name="review_threshold"
              value={4}
              checked={campaignData.positive_threshold === 4}
              onChange={(e) => onDataChange('positive_threshold', parseInt(e.target.value))}
              className="mr-3 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">4 or 5 Stars (Recommend)</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="radio"
              name="review_threshold"
              value={5}
              checked={campaignData.positive_threshold === 5}
              onChange={(e) => onDataChange('positive_threshold', parseInt(e.target.value))}
              className="mr-3 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">5 Stars</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="radio"
              name="review_threshold"
              value={3}
              checked={campaignData.positive_threshold === 3}
              onChange={(e) => onDataChange('positive_threshold', parseInt(e.target.value))}
              className="mr-3 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">3 or more Stars</span>
          </label>
        </div>
      </div>

      {/* Title Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Title</h4>
        <input
          type="text"
          value={campaignData.title || 'Review Request'}
          onChange={(e) => onDataChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Review Request"
        />
      </div>

      {/* Comment Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Add your comment</h4>
        <textarea
          value={campaignData.intro_text || 'Your feedback is important to us. Please take a moment to review us.'}
          onChange={(e) => onDataChange('intro_text', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          placeholder="Your feedback is important to us. Please take a moment to review us."
        />
      </div>

      {/* Star Rating Questions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">Star Rating Questions</h4>
          <div className="text-sm text-gray-500">
            {getEnabledQuestionsCount()}/4 questions enabled
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Choose which questions to display (minimum 2, maximum 4). Overall Experience cannot be hidden.
        </p>

        {/* Validation Error */}
        {validationError && (
          <div className="mb-4 flex items-center text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
            <Icon name="AlertCircle" size={16} className="mr-2" />
            {validationError}
          </div>
        )}

        <div className="space-y-4">
          {predefinedQuestions.map((question) => (
            <div key={question.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{question.label}</div>
                {question.required && (
                  <div className="text-xs text-red-600 mt-1">Required</div>
                )}
              </div>
              
              <div className="flex items-center">
                <button
                  onClick={() => handleQuestionToggle(question.id)}
                  disabled={question.alwaysEnabled || (enabledQuestions.length <= 2 && enabledQuestions.includes(question.id)) || (enabledQuestions.length >= 4 && !enabledQuestions.includes(question.id))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                    enabledQuestions.includes(question.id)
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  } ${question.alwaysEnabled || (enabledQuestions.length <= 2 && enabledQuestions.includes(question.id)) || (enabledQuestions.length >= 4 && !enabledQuestions.includes(question.id)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      enabledQuestions.includes(question.id) ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <div className="font-medium mb-1">Question Requirements:</div>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Overall Experience is always required and cannot be hidden</li>
                <li>Minimum 2 questions total (including Overall Experience)</li>
                <li>Maximum 4 questions can be enabled</li>
                <li>All questions use 5-star rating scale</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button 
            variant="primary"
            disabled={isLoading || !!validationError}
            onClick={async () => {
              // Final validation before saving
              if (!validateQuestionSelection(enabledQuestions)) {
                if (onMessage) {
                  onMessage('error', 'Please fix validation errors before saving.');
                }
                return;
              }

              setIsLoading(true);
              try {
                if (onSave) {
                  // Pass step-specific data
                  const stepData = {
                    questions: campaignData.questions,
                    positive_threshold: campaignData.positive_threshold
                  };
                  
                  // Debug logging
                  console.log('Step 1 Save - Full campaign data:', campaignData);
                  console.log('Step 1 Save - Questions being saved:', stepData.questions);
                  console.log('Step 1 Save - Enabled questions:', stepData.questions?.filter(q => q.enabled));
                  
                  await onSave(stepData);
                }
                if (onMessage) {
                  onMessage('success', 'Review settings saved successfully!');
                }
              } catch (error) {
                console.error('Save error:', error);
                if (onMessage) {
                  onMessage('error', 'Failed to save settings. Please try again.');
                }
              } finally {
                setIsLoading(false);
              }
            }}
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>

    </div>
  );
};

export default StepReviewQuestions; 